import React from 'react';
import { UserProfile } from '@/shared/types';
import {
  Building2,
  DollarSign,
  Users,
  FileText,
  Receipt,
  TrendingUp,
  Calendar,
  Shield,
  AlertCircle,
  Trash2,
  Edit,
  Mail,
  Phone,
  MapPin
} from 'lucide-react';
import { Modal, Button, Badge, Avatar } from '@/shared/components/ui';
import { useUserStats } from '@/features/admin/hooks/useUserStats';
import { useUserActivity } from '@/features/admin/hooks/useUserActivity';

interface UserPreviewModalProps {
  user: UserProfile | null;
  onClose: () => void;
  onEditPlan: () => void;
  onEditRole?: (userId: string) => void;
  onSuspendAccount?: (userId: string) => void;
  onDeleteAccount?: (userId: string) => void;
}

/**
 * UserPreviewModal - Admin modal for viewing detailed user information
 * Displays user profile, account statistics, subscription, and recent activity
 * Supports admin actions: edit role, change subscription, suspend, delete
 */
export const UserPreviewModal: React.FC<UserPreviewModalProps> = ({
  user,
  onClose,
  onEditPlan,
  onEditRole,
  onSuspendAccount,
  onDeleteAccount
}) => {
  if (!user) return null;

  // Fetch user statistics from database
  const { stats, loading: statsLoading } = useUserStats(user.id);
  // Fetch recent user activity from database
  const { activity, loading: activityLoading } = useUserActivity(user.id);

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: user.currency || 'USD'
    }).format(amount);
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      size="full"
      title="User Details"
      footer={
        <div className="flex justify-between items-center">
          <div className="flex gap-2">
            {onEditRole && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onEditRole(user.id)}
                className="text-gray-600 hover:text-gray-800"
              >
                <Edit size={12} className="mr-1" />
                Edit Role
              </Button>
            )}
            {onSuspendAccount && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onSuspendAccount(user.id)}
                className="text-amber-600 hover:text-amber-800 border-amber-300 hover:border-amber-400"
              >
                <AlertCircle size={12} className="mr-1" />
                Suspend
              </Button>
            )}
            {onDeleteAccount && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onDeleteAccount(user.id)}
                className="text-red-600 hover:text-red-800 border-red-300 hover:border-red-400"
              >
                <Trash2 size={12} className="mr-1" />
                Delete
              </Button>
            )}
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={onEditPlan}>
              Change Subscription
            </Button>
            <Button variant="primary" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-5">
        {/* User Profile Header */}
        <div className="bg-gradient-to-r from-gray-50 to-white p-4 rounded border border-gray-200">
          <div className="flex items-start gap-3">
            <Avatar
              src={user.avatarUrl || undefined}
              alt={user.name}
              size="lg"
              className="ring-2 ring-white shadow"
            >
              {user.name.charAt(0).toUpperCase()}
            </Avatar>
            <div className="flex-1">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-base font-semibold text-gray-900">{user.name}</h3>
                  <p className="text-gray-600 flex items-center gap-1.5 mt-1 text-xs">
                    <Mail size={12} />
                    {user.email}
                  </p>
                </div>
                <Badge variant={user.role === 'admin' ? 'primary' : 'default'} size="sm">
                  <Shield size={12} className="mr-1" />
                  {user.role.toUpperCase()}
                </Badge>
              </div>
              <div className="flex flex-wrap gap-3 mt-2 text-xs text-gray-600">
                {user.businessPhone && (
                  <span className="flex items-center gap-1">
                    <Phone size={12} />
                    {user.businessPhone}
                  </span>
                )}
                {user.businessAddress && (
                  <span className="flex items-center gap-1">
                    <MapPin size={12} />
                    {user.businessAddress}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Account Statistics */}
        <div>
          <h4 className="text-xs font-medium text-gray-500 uppercase mb-3">Account Statistics</h4>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div className="bg-white p-3 rounded border border-gray-200">
              <div className="flex items-center gap-1.5 mb-1">
                <Users className="text-primary-600" size={14} />
                <span className="text-xs text-gray-500 uppercase font-medium">Clients</span>
              </div>
              <p className="text-base font-semibold text-gray-900">
                {statsLoading ? '...' : stats.totalClients}
              </p>
            </div>
            <div className="bg-white p-3 rounded border border-gray-200">
              <div className="flex items-center gap-1.5 mb-1">
                <FileText className="text-blue-600" size={14} />
                <span className="text-xs text-gray-500 uppercase font-medium">Quotes</span>
              </div>
              <p className="text-base font-semibold text-gray-900">
                {statsLoading ? '...' : stats.totalQuotes}
              </p>
            </div>
            <div className="bg-white p-3 rounded border border-gray-200">
              <div className="flex items-center gap-1.5 mb-1">
                <Receipt className="text-green-600" size={14} />
                <span className="text-xs text-gray-500 uppercase font-medium">Invoices</span>
              </div>
              <p className="text-base font-semibold text-gray-900">
                {statsLoading ? '...' : stats.totalInvoices}
              </p>
            </div>
            <div className="bg-white p-3 rounded border border-gray-200">
              <div className="flex items-center gap-1.5 mb-1">
                <TrendingUp className="text-emerald-600" size={14} />
                <span className="text-xs text-gray-500 uppercase font-medium">Revenue</span>
              </div>
              <p className="text-base font-semibold text-gray-900">
                {statsLoading ? '...' : formatCurrency(stats.totalRevenue)}
              </p>
            </div>
            <div className="bg-white p-3 rounded border border-gray-200">
              <div className="flex items-center gap-1.5 mb-1">
                <DollarSign className="text-purple-600" size={14} />
                <span className="text-xs text-gray-500 uppercase font-medium">Payments</span>
              </div>
              <p className="text-base font-semibold text-gray-900">
                {statsLoading ? '...' : formatCurrency(stats.totalPayments)}
              </p>
            </div>
          </div>
        </div>

        {/* Subscription Details */}
        <div className="bg-white p-4 rounded border border-gray-200">
          <h4 className="text-xs font-medium text-gray-400 uppercase mb-3 flex items-center gap-1.5">
            <Building2 size={12} />
            Subscription Details
          </h4>
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <p className="text-base font-semibold text-gray-900 capitalize">
                  {user.subscription.planId}
                </p>
                <Badge variant={user.subscription.status === 'active' ? 'success' : 'error'} size="xs">
                  {user.subscription.status}
                </Badge>
              </div>
              <p className="text-xs text-gray-500 capitalize mt-0.5">
                {user.subscription.billingPeriod} billing
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-500">Next Billing</p>
              <p className="text-sm font-medium text-gray-900">
                {formatDate(user.subscription.nextBillingDate)}
              </p>
            </div>
          </div>
        </div>

        {/* Account Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="bg-white p-4 rounded border border-gray-200">
            <h4 className="text-xs font-medium text-gray-400 uppercase mb-3 flex items-center gap-1.5">
              <Calendar size={12} />
              Account Dates
            </h4>
            <div className="space-y-2">
              <div>
                <span className="text-xs text-gray-400 block">Account Created</span>
                <p className="font-medium text-gray-900 text-sm">{formatDate(user.joinedAt)}</p>
              </div>
              <div>
                <span className="text-xs text-gray-400 block">Last Login</span>
                <p className="font-medium text-gray-900 text-sm">Not tracked</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded border border-gray-200">
            <h4 className="text-xs font-medium text-gray-400 uppercase mb-3">Business Profile</h4>
            <div className="space-y-2">
              <div>
                <span className="text-xs text-gray-400 block">Trading Name</span>
                <p className="font-medium text-gray-900 text-sm">{user.businessName || 'N/A'}</p>
              </div>
              <div>
                <span className="text-xs text-gray-400 block">Business Email</span>
                <p className="font-medium text-gray-900 text-sm">{user.businessEmail || user.email}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div>
          <h4 className="text-xs font-medium text-gray-500 uppercase mb-3">Recent Activity</h4>
          {activityLoading ? (
            <div className="text-center py-6 text-gray-500 text-sm">Loading activity...</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {/* Recent Quotes */}
              <div className="bg-white p-3 rounded border border-gray-200">
                <h5 className="text-xs font-medium text-gray-500 uppercase mb-2">
                  Recent Quotes
                </h5>
                {activity.recentQuotes.length === 0 ? (
                  <p className="text-xs text-gray-400">No quotes yet</p>
                ) : (
                  <div className="space-y-2">
                    {activity.recentQuotes.slice(0, 3).map((quote) => (
                      <div key={quote.id} className="text-xs border-b border-gray-100 pb-2 last:border-0">
                        <div className="flex justify-between items-start">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 truncate">{quote.title}</p>
                            <p className="text-xs text-gray-500">#{quote.quote_number}</p>
                          </div>
                          <span className="ml-2 text-xs font-medium text-gray-700">
                            {formatCurrency(parseFloat(quote.total_amount.toString()))}
                          </span>
                        </div>
                        <div className="flex justify-between items-center mt-1">
                          <Badge variant={quote.status === 'accepted' ? 'success' : 'default'} size="xs">
                            {quote.status}
                          </Badge>
                          <span className="text-xs text-gray-400">
                            {formatDate(quote.created_at)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Recent Invoices */}
              <div className="bg-white p-3 rounded border border-gray-200">
                <h5 className="text-xs font-medium text-gray-500 uppercase mb-2">
                  Recent Invoices
                </h5>
                {activity.recentInvoices.length === 0 ? (
                  <p className="text-xs text-gray-400">No invoices yet</p>
                ) : (
                  <div className="space-y-2">
                    {activity.recentInvoices.slice(0, 3).map((invoice) => (
                      <div key={invoice.id} className="text-xs border-b border-gray-100 pb-2 last:border-0">
                        <div className="flex justify-between items-start">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 truncate">{invoice.title}</p>
                            <p className="text-xs text-gray-500">#{invoice.invoice_number}</p>
                          </div>
                          <span className="ml-2 text-xs font-medium text-gray-700">
                            {formatCurrency(parseFloat(invoice.total_amount.toString()))}
                          </span>
                        </div>
                        <div className="flex justify-between items-center mt-1">
                          <Badge variant={invoice.status === 'paid' ? 'success' : invoice.status === 'overdue' ? 'error' : 'default'} size="xs">
                            {invoice.status}
                          </Badge>
                          <span className="text-xs text-gray-400">
                            {formatDate(invoice.created_at)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Recent Payments */}
              <div className="bg-white p-3 rounded border border-gray-200">
                <h5 className="text-xs font-medium text-gray-500 uppercase mb-2">
                  Recent Payments
                </h5>
                {activity.recentPayments.length === 0 ? (
                  <p className="text-xs text-gray-400">No payments yet</p>
                ) : (
                  <div className="space-y-2">
                    {activity.recentPayments.slice(0, 3).map((payment) => (
                      <div key={payment.id} className="text-xs border-b border-gray-100 pb-2 last:border-0">
                        <div className="flex justify-between items-center">
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">Payment Received</p>
                          </div>
                          <span className="text-xs font-medium text-green-600">
                            {formatCurrency(parseFloat(payment.amount.toString()))}
                          </span>
                        </div>
                        <div className="flex justify-between items-center mt-1">
                          <Badge variant={payment.status === 'completed' ? 'success' : 'default'} size="xs">
                            {payment.status}
                          </Badge>
                          <span className="text-xs text-gray-400">
                            {formatDate(payment.payment_date)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};
